import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { motion } from "framer-motion";

const data = [
  { score: "0-20", range: "0-20", count: 12, label: "Poor" },
  { score: "21-40", range: "21-40", count: 28, label: "Below Average" },
  { score: "41-60", range: "41-60", count: 52, label: "Average" },
  { score: "61-80", range: "61-80", count: 150, label: "Good" },
  { score: "81-100", range: "81-100", count: 58, label: "Excellent" },
];

// Custom tooltip for better data visualization
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    const totalCandidates = data.reduce((sum, entry) => sum + entry.count, 0);
    const percentage = ((item.count / totalCandidates) * 100).toFixed(1);

    return (
      <div className="rounded-md border border-neutral-200 bg-white p-3 shadow-md">
        <h3 className="mb-1 font-medium">{item.label} ({item.range})</h3>
        <div className="space-y-1 text-sm">
          <div className="flex items-center justify-between gap-4">
            <span className="text-neutral-500">Candidates:</span>
            <span className="font-medium">{item.count}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-neutral-500">Percentage:</span>
            <span className="font-medium">{percentage}%</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

// Color generator based on value
const getBarColor = (score) => {
  if (!score || typeof score !== "string") return "#ccc";
  const [min, max] = score.split("-").map(Number);
  if (isNaN(min) || isNaN(max)) return "#ccc";

  const midpoint = (min + max) / 2;

  if (midpoint < 30) return "#ef4444";
  if (midpoint < 50) return "#FF6B00";
  if (midpoint < 70) return "#0A66C2";
  if (midpoint < 90) return "#22c55e";
  return "#15a349";
};

export function ScoreDistributionChart() {
  // Calculate total candidates for title
  const totalCandidates = data.reduce((sum, entry) => sum + entry.count, 0);

  // Animation variants for bars
  const barVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: i => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };

  return (
    <div className="flex h-full w-full flex-col">
      <div className="mb-2 text-center">
        <span className="text-sm font-medium text-neutral-600">
          Score Distribution ({totalCandidates} Candidates)
        </span>
      </div>

      <ResponsiveContainer height={200} width="100%">
        <BarChart
          data={data}
          margin={{
            top: 5,
            right: 15,
            left: 0,
            bottom: 20,
          }}
          barSize={36}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
          <XAxis
            dataKey="score"
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#6b7280', fontSize: 11 }}
            label={{
              value: "Score Range",
              position: "bottom",
              offset: 0,
              fontSize: 12,
              fill: '#6b7280'
            }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#6b7280', fontSize: 11 }}
            width={30}
            label={{
              value: "Candidates",
              angle: -90,
              position: "insideLeft",
              offset: -5,
              fontSize: 12,
              fill: '#6b7280'
            }}
          />
          <Tooltip
            content={<CustomTooltip active={undefined} payload={undefined} label={undefined} />}
            cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
          />
          <Bar
            dataKey="count"
            radius={[4, 4, 0, 0]}
            animationDuration={1000}
            animationBegin={0}
          >
            {data.map((entry, index) => (
              <motion.g
                key={`cell-${index}`}
                custom={index}
                initial="hidden"
                animate="visible"
                variants={barVariants}
              >
                <Cell
                  key={`cell-${index}`}
                  fill={getBarColor(entry.score)}
                  // Add gradient effect
                  fillOpacity={0.9}
                  stroke={getBarColor(entry.score)}
                  strokeWidth={1}
                />
              </motion.g>
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Score quality legend */}
      <div className="mt-1 flex justify-center">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-600">
          <div className="flex items-center">
            <div className="mr-1 h-2 w-2 rounded-full bg-[#ef4444]"></div>
            <span className="text-xs">Poor</span>
          </div>
          <div className="flex items-center">
            <div className="mr-1 h-2 w-2 rounded-full bg-[#FF6B00]"></div>
            <span className="text-xs">Below Avg</span>
          </div>
          <div className="flex items-center">
            <div className="mr-1 h-2 w-2 rounded-full bg-[#0A66C2]"></div>
            <span className="text-xs">Average</span>
          </div>
          <div className="flex items-center">
            <div className="mr-1 h-2 w-2 rounded-full bg-[#22c55e]"></div>
            <span className="text-xs">Good</span>
          </div>
          <div className="flex items-center">
            <div className="mr-1 h-2 w-2 rounded-full bg-[#15a349]"></div>
            <span className="text-xs">Excellent</span>
          </div>
        </div>
      </div>
    </div>
  );
}
